import { Box } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { patientsSocialHistoryApi } from "apis";
import React from "react";
import { SOCIAL_HISTORY_FIELDS } from "types";

import { PATIENT_SECTIONS } from "./detail/PatientDetail";
import IntakeField from "./IntakeField";
import IntakeHeader from "./IntakeHeader";
import LayoutWrapper from "./LayoutWrapper";

export const SocialHistory: React.FC<{ patientId: string }> = ({
  patientId,
}) => {
  const { data, isError, error } = useQuery({
    queryKey: ["socialHistory", patientId],
    queryFn: async () => {
      if (!patientId) return null;
      return patientsSocialHistoryApi.getSocialHistoryByPatientId({
        patientId,
      });
    },
    enabled: !!patientId,
  });
  React.useEffect(() => {
    if (isError && error) {
      console.error("SocialHistory query error:", error);
    }
  }, [isError, error]);
  const [hasChildren, setHasChildren] = React.useState(
    data?.hasChildren ?? false
  );

  React.useEffect(() => {
    setHasChildren(data?.hasChildren ?? false);
  }, [data?.hasChildren]);

  const displayData = {
    relationship: data?.relationship,
    hasChildren: data?.hasChildren,
    numberOfChildren: data?.numberOfChildren,
    agesOfChildren: data?.agesOfChildren,
    tobaccoUse: data?.tobaccoUse,
    alcoholUse: data?.alcoholUse,
    cannabisOrDrugUse: data?.cannabisOrDrugUse,
    caffeineUse: data?.caffeineUse,
    sleepHours: data?.sleepHours,
    sleepQuality: data?.sleepQuality,
    sleepTrouble: data?.sleepTrouble,
    physicalActivityFrequency: data?.physicalActivityFrequency,
    physicalActivityType: data?.physicalActivityType,
    physicalActivityMinutesPerSession: data?.physicalActivityMinutesPerSession,
    stressLevel: data?.stressLevel,
    stressSource: data?.stressSource,
    stressCopingMethods: data?.stressCopingMethods,
    hasSupportSystem: data?.hasSupportSystem,
    additionalInformation: data?.additionalInformation,
  };

  const renderField = (metric: (typeof SOCIAL_HISTORY_FIELDS)[number]) => {
    if (
      !hasChildren &&
      (metric.key === "numberOfChildren" || metric.key === "agesOfChildren")
    ) {
      return;
    }
    const value = displayData[metric.key as keyof typeof displayData] as
      | string
      | number
      | boolean;

    const formattedValue = [
      "sleepTrouble",
      "hasChildren",
      "hasSupportSystem",
    ].includes(metric.key)
      ? value === true
        ? "Yes"
        : value === false
          ? "No"
          : "N/A"
      : (value as string);

    return (
      <IntakeField
        label={metric.label}
        value={formattedValue}
        disabled={true}
      />
    );
  };

  return (
    <Box sx={{ mt: 2 }}>
      <IntakeHeader title={PATIENT_SECTIONS.socialHistory.label} />

      <LayoutWrapper itemsPerRow={2} spacing={3}>
        {SOCIAL_HISTORY_FIELDS.filter(
          (field) => field.key !== "additionalInformation"
        ).map(renderField)}
      </LayoutWrapper>

      <IntakeField
        label="Additional Information"
        value={displayData.additionalInformation ?? ""}
        disabled={true}
        rows={3}
      />
    </Box>
  );
};

export default SocialHistory;
